/** What `GenerateRequest.currentOffer.id` (and `comparisonOffer.id`) identifies. */
export type TargetType = 'offer' | 'vendor';

export type DeckType =
  | 'post_campaign_standalone'
  | 'post_campaign_comparison'
  | 'post_campaign_standalone_with_comparison_notes'
  | 'post_campaign_experience'
  | 'post_campaign_comparison_experience'
  | 'post_campaign_vendor_experience';

export const DECK_TYPES: Array<DeckType> = [
  'post_campaign_standalone',
  'post_campaign_comparison',
  'post_campaign_standalone_with_comparison_notes',
  'post_campaign_experience',
  'post_campaign_comparison_experience',
  'post_campaign_vendor_experience',
];

/** Deck types that require a second comparison offer + schedule (same property for Hotels; same vendor currency for Experiences). */
export const COMPARISON_DECK_TYPES: Array<DeckType> = [
  'post_campaign_comparison',
  'post_campaign_standalone_with_comparison_notes',
  'post_campaign_comparison_experience',
];

/**
 * Display-label overrides for deck types whose sentence-cased `deck_type` key alone would
 * be misleading. `post_campaign_experience` is the standalone-only counterpart of
 * `post_campaign_comparison_experience` (mirroring the Hotels standalone/comparison split),
 * so its display label spells that out even though the wire value stays `post_campaign_experience`.
 * `post_campaign_comparison_experience`'s sentence-cased name reads as ambiguous next to the
 * standalone one, so it gets a shorter, clearer label; the wire value is unchanged.
 */
const DECK_TYPE_LABEL_OVERRIDES: Partial<Record<DeckType, string>> = {
  post_campaign_experience: 'Post campaign experience standalone',
  post_campaign_comparison_experience: 'Post campaign experience with comparison notes',
  post_campaign_vendor_experience: 'Post campaign experience by vendor over period',
};

/** Sentence-case a raw deck_type key for display, e.g. `post_campaign_comparison` → `Post campaign comparison`. */
export function formatDeckType(deckType: DeckType): string {
  const override = DECK_TYPE_LABEL_OVERRIDES[deckType];
  if (override) {
    return override;
  }
  const spaced = deckType.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export type MetricKey =
  | 'room_nights'
  | 'vendor_revenue_ar'
  | 'bookings'
  | 'packages_sold'
  | 'alos'
  | 'avg_lead_time'
  | 'upgraded_packages_pct'
  | 'avg_daily_rate'
  | 'number_of_views';

export const METRIC_OPTIONS: Array<{ key: MetricKey; label: string }> = [
  { key: 'room_nights', label: 'Room Nights' },
  { key: 'vendor_revenue_ar', label: 'Vendor Revenue (AR)' },
  { key: 'bookings', label: 'Bookings' },
  { key: 'packages_sold', label: 'Packages Sold' },
  { key: 'alos', label: 'ALOS' },
  { key: 'avg_lead_time', label: 'Avg Lead Time' },
  { key: 'upgraded_packages_pct', label: '% Upgraded Packages' },
  { key: 'avg_daily_rate', label: 'Avg Daily Rate' },
  { key: 'number_of_views', label: 'Number of Views' },
];

export interface SelectedOffer {
  id: string;
  name: string;
  /** Canonical property key from svc-studio / dim_offer (same-property comparisons). */
  propertyId?: string;
  vendorName?: string;
  /** Vendor business key (Experiences only for now) — exact vendor-identity check for comparisons. */
  vendorBk?: string;
  locations?: Array<string>;
  productName?: string;
  offerStartDate?: string;
  offerEndDate?: string;
  /** Vendor's preferred currency (ISO 4217) — Tableau Vendor Report renders amounts in this currency. */
  vendorCurrency?: string;
  prevOfferBk?: string;
  prevOfferName?: string;
}

export interface OfferSchedule {
  startDate: string;
  endDate: string;
}

export interface GenerateRequest {
  deckType: DeckType;
  /** Omitted (defaults to 'offer') for every deck type except the vendor rollup. */
  targetType?: TargetType;
  currentOffer: SelectedOffer;
  /** Omitted for the standalone flow; required for the comparison flow. */
  comparisonOffer?: SelectedOffer;
  currentSchedule: OfferSchedule;
  /** Omitted for the standalone flow; required for the comparison flow. */
  comparisonSchedule?: OfferSchedule;
  commentary: string;
  metrics: Array<MetricKey>;
}

/**
 * PCM fact-check: values resolved or swapped during slide generation (BriefCase API contract).
 * Bump `factCheckVersion` on JobStatusResponse when this shape changes incompatibly.
 */
export type SlideFactCheckCategory = 'metric' | 'copy' | 'date' | 'chart' | 'other';

export type SlideFactCheckSource = 'api' | 'formula' | 'llm' | 'static' | string;

export interface SlideFactCheckEntry {
  id: string;
  label: string;
  category?: SlideFactCheckCategory;
  /** Placeholder or prior value before resolution. */
  before?: string | null;
  /** Final value shown on the slide. */
  after?: string | null;
  /** How the value was produced. */
  source?: SlideFactCheckSource;
  /** Optional trace (e.g. query id, dataset id). */
  evidence?: string | null;
  /** SQL executed to obtain the value (PCM audit). */
  sql?: string | null;
  /** BriefCase API (Pydantic): `derivation_steps` → `derivationSteps`. */
  derivationSteps?: Array<string>;
  /** Local/demo payloads only; prefer `derivationSteps` from the API. */
  derivation?: Array<string>;
}

export interface SlidePreview {
  index: number;
  imageAccessUrl: string;
  label?: string;
  /** Populated when the BriefCase API returns per-slide provenance for PCM review. */
  factCheck?: Array<SlideFactCheckEntry>;
}

export type JobStatus = 'queued' | 'generating' | 'complete' | 'failed';

export type JobStage =
  | 'queued'
  | 'querying_bq'
  | 'calling_llm'
  | 'building_pptx'
  | 'exporting_pngs'
  | 'uploading'
  | 'complete';

/** Response from POST /api/studio/generate — async submit. */
export interface JobSubmitResponse {
  jobId: string;
  status: JobStatus;
}

/** Response from GET /api/studio/jobs/{jobId} — polled until status is 'complete' or 'failed'. */
export interface JobStatusResponse {
  jobId: string;
  status: JobStatus;
  stage?: JobStage | null;
  error?: string | null;
  slides: Array<SlidePreview>;
  /** Populated when complete; null/omitted earlier. */
  downloadAccessUrl?: string | null;
  /** Optional contract version for `factCheck` entries on slides. */
  factCheckVersion?: number;
  /** Originating GenerateRequest, echoed back so cold visitors to a shared link can render the meta panel. */
  request?: GenerateRequest | null;
}

/**
 * Outcome of a *completed* refine — svc-studio contract on `RefineResponse.outcome`.
 * Only set when `status === 'complete'`; cancelled/failed refines never reach the
 * LLM classification step that populates this.
 * - `applied`: shape edits patched into the deck, image re-rendered.
 * - `applied_with_caveats`: partial success — some edits applied, others not; surface caveats[].
 * - `out_of_scope`: refine doesn't support the request (e.g. layout/chart-type changes); deck is byte-identical.
 */
export type RefineOutcome = 'applied' | 'applied_with_caveats' | 'out_of_scope';

/**
 * FE-local iteration outcome. Overlays the two non-complete terminal statuses
 * (`cancelled`, `failed`) onto the BE outcome so every iteration card has a
 * first-class terminal state. The mapping from poll response is:
 *   status=complete   → outcome from RefineResponse.outcome (or 'applied' if absent)
 *   status=cancelled  → 'cancelled'  (BE-emitted: supersede on the same slide, or app shutdown)
 *   status=failed     → 'failed'     (BE pipeline error)
 * Network errors / aborts / timeouts surface as mutation errors, not iteration outcomes.
 */
export type IterationOutcome = RefineOutcome | 'cancelled' | 'failed';

interface SlideRefineResponseBase {
  index: number;
  /** Short user-facing sentence ("Shortened title to 95 chars"). For cancelled/failed, the BE's `error` text. */
  summary?: string | null;
  /** Things the LLM couldn't do or partially did; rendered as a bulleted list under the summary. */
  caveats?: Array<string> | null;
}

/**
 * A refine that ran to completion (BE `status: complete`). Image URL is guaranteed
 * present and slide-level state should be updated.
 */
export interface SlideRefineCompleted extends SlideRefineResponseBase {
  outcome: RefineOutcome;
  imageAccessUrl: string;
  /** Template-fill refine returns updated shape texts. */
  shapes?: Record<string, string> | null;
  /** Replaces fact-check entries for this slide after refine (omit to keep previous entries). */
  factCheck?: Array<SlideFactCheckEntry> | null;
}

/**
 * A refine that terminated before producing output (BE `status: cancelled | failed`).
 * No image change; callers must not feed this into slide-level state.
 */
export interface SlideRefineTerminated extends SlideRefineResponseBase {
  outcome: 'cancelled' | 'failed';
  imageAccessUrl: null;
}

/**
 * Discriminated union on `outcome`. Use the `isRefineCompleted` type guard to narrow
 * at consumer sites — TypeScript will then enforce that callers expecting an
 * `imageAccessUrl: string` (e.g. `onSlideRefined` consumers) only see the completed variant.
 */
export type SlideRefineResponse = SlideRefineCompleted | SlideRefineTerminated;

/** Type guard: true when the refine completed and `imageAccessUrl` / `shapes` / `factCheck` are populated. */
export function isRefineCompleted(r: SlideRefineResponse): r is SlideRefineCompleted {
  return r.outcome !== 'cancelled' && r.outcome !== 'failed';
}

export type RefineJobStatus = 'queued' | 'running' | 'complete' | 'failed' | 'cancelled';

/** 202 response from PATCH /api/studio/decks/{jobId}/slides/{index} — refine is queued. */
export interface RefineSubmitResponse {
  refineJobId: string;
  jobId: string;
  slideIndex: number;
  status: RefineJobStatus;
}

/** Poll response from GET /api/studio/decks/{jobId}/slides/{index}/refines/{refineId}. */
export interface RefineStatusResponse {
  refineJobId: string;
  jobId: string;
  slideIndex: number;
  status: RefineJobStatus;
  error?: string | null;
  shapes?: Record<string, string> | null;
  imageAccessUrl?: string | null;
  factCheck?: Array<SlideFactCheckEntry> | null;
  outcome?: RefineOutcome | null;
  summary?: string | null;
  caveats?: Array<string> | null;
}

export interface DeckMetadata {
  title: string;
  subtitle: string;
  slideCount: number;
  stepLabel: string;
}

export type AIReviewItemType = 'suggestion' | 'quality_check';
export type AIReviewStatus = 'pass' | 'fail' | 'suggestion';

export interface AIReviewItem {
  id: string;
  type: AIReviewItemType;
  message: string;
  status: AIReviewStatus;
}

// ---------------------------------------------------------------------------
// History (localStorage)
// ---------------------------------------------------------------------------

export const BRIEFCASE_HISTORY_KEY = 'briefcase_history';
export const BRIEFCASE_HISTORY_MAX = 10;

export interface HistoryEntry {
  id: string; // jobId or timestamp-based
  offerName: string;
  request: GenerateRequest;
  createdAt: string; // ISO 8601
}

export function loadHistory(): Array<HistoryEntry> {
  try {
    const raw = localStorage.getItem(BRIEFCASE_HISTORY_KEY);
    return raw ? (JSON.parse(raw) as Array<HistoryEntry>) : [];
  } catch {
    return [];
  }
}

export function saveHistoryEntry(entry: HistoryEntry): void {
  const history = loadHistory().filter((h) => h.id !== entry.id);
  history.unshift(entry);
  if (history.length > BRIEFCASE_HISTORY_MAX) {
    history.length = BRIEFCASE_HISTORY_MAX;
  }
  localStorage.setItem(BRIEFCASE_HISTORY_KEY, JSON.stringify(history));
}
